import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import {
  Button,
  ButtonBlock,
  List,
  Section,
  Tab,
  useToast,
} from '@politechdev/blocks-design-system'
import { CardError } from 'components'
import { useRequest } from 'hooks'
import {
  getAssociatedFlags,
  getAssociatedShifts,
  navigateToFlag,
  navigateToPacket,
} from 'performanceReviews/utils'
import {
  AssociatedFlag,
  AssociatedShift,
  PerformanceReview,
  SubmittablePerformanceReview,
  UpdateReviewRequestResult,
} from 'performanceReviews/types'
import FlagListItem from './FlagListItem'
import AddFlagsModal from './AddFlagsModal'
import ShiftListItem from './ShiftListItem'
import AddShiftsModal from './AddShiftsModal'
import styles from './AssociatedRecordTabs.module.scss'

const AssociatedRecordTabs = ({
  performanceReview,
  updateReview,
}: {
  performanceReview: PerformanceReview
  updateReview: (
    fields: SubmittablePerformanceReview
  ) => UpdateReviewRequestResult
}) => {
  const { t } = useTranslation()
  const { setToast } = useToast()
  const { id: idString } = useParams<{ id: string }>()

  const [isAddFlagsOpen, setIsAddFlagsOpen] = useState(false)
  const [isAddShiftsOpen, setIsAddShiftsOpen] = useState(false)
  const [isUpdateLoading, setIsUpdateLoading] = useState(false)

  const getAssociatedFlagsRequest = useRequest(getAssociatedFlags, {
    onError: () => {
      setToast({
        message: 'An issue occured fetching associated flags',
        variant: 'error',
      })
    },
  })

  const getAssociatedShiftsRequest = useRequest(getAssociatedShifts, {
    onError: () => {
      setToast({
        message: 'An issue occured fetching associated packets',
        variant: 'error',
      })
    },
  })

  useEffect(() => {
    void getAssociatedShiftsRequest.makeRequest(+idString)
    void getAssociatedFlagsRequest.makeRequest(+idString)
  }, [idString])

  const handleReviewUpdate = async (updates: SubmittablePerformanceReview) => {
    setIsUpdateLoading(true)
    const response = await updateReview(updates)
    if (response?.performance_review) {
      if (updates.shift_ids) {
        void getAssociatedShiftsRequest.makeRequest(+idString)
      }
      if (updates.flag_ids) {
        void getAssociatedFlagsRequest.makeRequest(+idString)
      }
    }
    setIsUpdateLoading(false)
    return response
  }

  const handleRemoveFlag = (flagId: number) => {
    const updatedFlagIds = performanceReview
      .flags!.filter(flag => flagId !== flag.id)
      .map(({ id }) => id)
    void handleReviewUpdate({ flag_ids: updatedFlagIds })
  }

  const handleRemoveShift = (shiftId: number) => {
    const updatedShiftIds = performanceReview
      .shifts!.filter(shift => shiftId !== shift.id)
      .map(({ id }) => id)
    void handleReviewUpdate({ shift_ids: updatedShiftIds })
  }

  if (
    getAssociatedFlagsRequest.hasErrors ||
    getAssociatedShiftsRequest.hasErrors
  ) {
    return (
      <CardError
        hide={false}
        message={t(
          'An error ocurred while trying to fetch the records associated with this performance review.'
        )}
      />
    )
  }

  return (
    <Section label={t('Investigation')} secondary>
      <Tab.Container onChange={() => {}} className={styles['tab-container']}>
        <Tab label={t('Flags')}>
          <List
            loading={getAssociatedFlagsRequest.isLoading || isUpdateLoading}
            emptyMessage={t('No associated flags')}
            itemData={
              getAssociatedFlagsRequest.response?.['quality_control/flags'] ??
              []
            }
            render={item => {
              const flag = item as AssociatedFlag
              return (
                <FlagListItem key={flag.id} flag={flag}>
                  <Button.Secondary onClick={() => handleRemoveFlag(flag.id)}>
                    {t('Remove')}
                  </Button.Secondary>
                  <Button onClick={() => navigateToFlag(flag)}>
                    {t('View')}
                  </Button>
                </FlagListItem>
              )
            }}
          />
          <ButtonBlock>
            <Button.Secondary onClick={() => setIsAddFlagsOpen(true)}>
              {t('Add more flags')}
            </Button.Secondary>
          </ButtonBlock>
          <AddFlagsModal
            isOpen={isAddFlagsOpen}
            setIsOpen={setIsAddFlagsOpen}
            performanceReview={performanceReview}
            updateReview={handleReviewUpdate}
          />
        </Tab>
        <Tab label={t('Packets')}>
          <List
            loading={getAssociatedShiftsRequest.isLoading || isUpdateLoading}
            emptyMessage={t('No associated packets')}
            itemData={getAssociatedShiftsRequest.response?.shifts ?? []}
            render={item => {
              const shift = item as AssociatedShift
              return (
                <ShiftListItem key={shift.id} shift={shift}>
                  <Button.Secondary onClick={() => handleRemoveShift(shift.id)}>
                    {t('Remove')}
                  </Button.Secondary>
                  <Button onClick={() => navigateToPacket(shift)}>
                    {t('View')}
                  </Button>
                </ShiftListItem>
              )
            }}
          />
          <ButtonBlock>
            <Button.Secondary onClick={() => setIsAddShiftsOpen(true)}>
              {t('Add more packets')}
            </Button.Secondary>
          </ButtonBlock>
          <AddShiftsModal
            isOpen={isAddShiftsOpen}
            setIsOpen={setIsAddShiftsOpen}
            performanceReview={performanceReview}
            updateReview={handleReviewUpdate}
          />
        </Tab>
      </Tab.Container>
    </Section>
  )
}

export default AssociatedRecordTabs
